import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input, AfterViewInit, OnDestroy, OnInit } from "@angular/core";

import { ArgTypes } from "@storybook/angular/types-6-0";
import { coerceNumberProperty, coerceBooleanProperty, BooleanInput, NumberInput } from "@vattenfall/util";
import { Subscription, ReplaySubject } from "rxjs";

import { TextsElementAccessor } from "../texts-accessor";

export const argTypesTextarea: ArgTypes = <const>{
  minRows: {
    control: "number",
    description: "Minimum number of rows the control can contract.",
    table: {
      category: "Inputs",
      type: {
        summary: "number",
      },
    },
  },
  maxRows: {
    control: "number",
    description: "Maximum number of rows the control can expand.",
    table: {
      category: "Inputs",
      type: {
        summary: "number",
      },
    },
  },
  resize: {
    control: "boolean",
    description: "Whether the user should be allowed to resize the textarea.",
    table: {
      category: "Inputs",
      type: {
        summary: "boolean",
      },
    },
  },
};

/**
 * Textarea fields show all user input at once. Overflow text causes the text field to expand (shifting screen elements downward), and text wraps onto a new line.
 *
 * Optionally, the number of rows in which the input should initially appear can be specified using the `rows` input.
 *
 * Text inputs can be used as an Angular `FormControl`, in both reactive and template driven forms.
 */
@Component({
  selector: "ui-input[type=textarea]",
  templateUrl: "./textarea.component.html",
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputTextareaComponent extends TextsElementAccessor implements AfterViewInit, OnDestroy, OnInit {
  /**
   * Minimum number of rows the control can contract.
   *
   * Default is `2`.
   */
  @Input()
  get minRows(): number {
    return this._minRows;
  }
  set minRows(value: NumberInput) {
    this._minRows = coerceNumberProperty(value);
    this.setProperty("rows", this._minRows);
  }
  private _minRows = 2;
  /**
   * Maximum number of rows the control can expand.
   *
   * Default is `Infinity`.
   */
  @Input()
  get maxRows(): number {
    return this._maxRows;
  }
  set maxRows(value: NumberInput) {
    this._maxRows = coerceNumberProperty(value);
  }
  private _maxRows: number = Number.POSITIVE_INFINITY;
  /**
   * Whether the user should be allowed to resize the textarea.
   *
   * Default is `true`.
   */
  @Input()
  get resize(): boolean {
    return this._resize;
  }
  set resize(value: BooleanInput) {
    this._resize = coerceBooleanProperty(value);
  }
  private _resize = false;

  private _setAutosizeSubject = new ReplaySubject<boolean>(Number.POSITIVE_INFINITY);
  private _setAutosizeSubscription = Subscription.EMPTY;
  public _ngControlSubscription = Subscription.EMPTY;

  // TODO (stefan): Check if removing this is causing regression
  // constructor(
  //   public ngControl: NgControl,
  //   protected renderer: Renderer2,
  //   public changeDetectorRef: ChangeDetectorRef,
  //   @Inject(DOCUMENT) private document: Document
  // ) {
  //   super(ngControl, renderer, changeDetectorRef);
  // }

  ngOnInit() {
    if (this.ngControl)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this._ngControlSubscription = this.ngControl.valueChanges!.pipe().subscribe(() => this._setAutosizeSubject.next());
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    this._setAutosizeSubscription = this._setAutosizeSubject.subscribe(() => {
      // Need to set size auto or else the textarea will not contract
      this.inputElement.nativeElement.style.height = "auto";
      const lineHeight = Number.parseInt(
        document.defaultView?.getComputedStyle(this.inputElement.nativeElement).lineHeight ?? ""
      );
      const maxHeight = lineHeight * this.maxRows + 22;
      const currentHeight = this.inputElement.nativeElement.scrollHeight || 43 + 2;
      this.inputElement.nativeElement.style.height = (currentHeight > maxHeight ? maxHeight : currentHeight) + "px";
    });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this._setAutosizeSubject.complete();
    this._setAutosizeSubscription.unsubscribe();
    this._ngControlSubscription.unsubscribe();
  }

  onInputChange(value: string) {
    this.onChange(value);
    // Avoid resizing twice if used as a FormControl
    if (!this.ngControl) this._setAutosizeSubject.next();
  }
}
